import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Flex,
  FormControl,
  Heading,
  Input,
  InputGroup,
  InputLeftElement,
  Stack,
  VStack,
  Text,
  useColorModeValue,
  Checkbox,
  Link as ChakraLink,
} from '@chakra-ui/react';
import { BsFileLockFill, BsPersonFill } from "react-icons/bs";
import { Link as ReactRouterLink } from 'react-router-dom';

import { isAuth } from '../../util/IsAuth';
import { post, AuthError } from '../../util/APIHelp'; // Import AuthError

const LoginPage: React.FC = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [usernameError, setUsernameError] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [formError, setFormError] = useState('');
  const navigate = useNavigate();

  const cardBg = useColorModeValue('white', 'gray.800');
  const panelBg = useColorModeValue('gray.100', 'gray.700');
  const inputBg = useColorModeValue('white', 'gray.600');
  const textColor = useColorModeValue('gray.800', 'whiteAlpha.900');

  // This useEffect ensures that if the user somehow lands on login page while authenticated, they are redirected.
  useEffect(() => {
    if (isAuth()) {
      navigate('/dashboard'); // Redirect to dashboard if already authenticated
    }
  }, [navigate]);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError('');
    setUsernameError('');
    setPasswordError('');

    let isValid = true;

    if (!username) {
      setUsernameError('Username is required');
      isValid = false;
    }

    if (!password) {
      setPasswordError("Password is required");
      isValid = false;
    }

    if (!isValid) {
      return;
    }

    try {
      const response = await post("/login", { username, password });
      const data = await response.json();
      console.log("json response: ", data);

      if (!response.ok) {
        throw new Error(data.message || 'An error occurred during login.');
      }

      if (rememberMe) {
        localStorage.setItem('rememberMeEmail', username);
      } else {
        localStorage.removeItem('rememberMeEmail');
      }

      localStorage.setItem('token', data.token);

      window.dispatchEvent(new StorageEvent('storage', {
        key: 'token',
        newValue: data.token
      }));

      navigate('/dashboard');
    } catch (error) {
      if (error instanceof AuthError) {
        setFormError(error.message);
      } else if (error instanceof Error) {
        setFormError(error.message);
      } else {
        setFormError('An unexpected error occurred.');
      }
    }
  };

  return (
    <Flex minH={'100vh'} align={'center'} justify={'center'}>
      <Box
        maxW={'600px'}
        w={'full'}
        bg={cardBg}
        boxShadow={'lg'}
        rounded={'lg'}
        overflow={'hidden'}
      >
        <VStack spacing={0} align="stretch">
          {/* Top Panel for Logo */}
          <Box
            h={{ base: '150px', md: '200px' }}
            w={'full'}
            bgImage={"url('assets/images/jpg/png/two_fish_logo.jpg')"}
            bgSize={'30%'}
            bgPos={'center'}
            bgRepeat={'no-repeat'}
          />

          {/* Bottom Panel for Form */}
          <Box bg={panelBg} w={'full'} p={{ base: 6, md: 8 }}>
            <Stack align={'center'} mb={6}>
              <Heading fontSize={{ base: 'xl', md: '2xl' }} color={textColor}>
                Automation Hub
              </Heading>
            </Stack>
            <form onSubmit={handleLogin}>
              <VStack spacing={4}>
                <FormControl id="username" isInvalid={!!usernameError}>
                  <InputGroup>
                    <InputLeftElement pointerEvents="none">
                      <BsPersonFill color="gray" />
                    </InputLeftElement>
                    <Input
                      type='text'
                      placeholder='Username'
                      value={username}
                      onChange={e => setUsername(e.target.value)}
                      bg={inputBg}
                    />
                  </InputGroup>
                  {usernameError && <Text color='red.500' fontSize="sm" mt={1}>{usernameError}</Text>}
                </FormControl>
                <FormControl id="password" isInvalid={!!passwordError}>
                  <InputGroup>
                    <InputLeftElement pointerEvents="none">
                       <BsFileLockFill color="gray" />
                    </InputLeftElement>
                    <Input
                      type='password'
                      placeholder='Password'
                      value={password}
                      onChange={e => setPassword(e.target.value)}
                      bg={inputBg}
                    />
                  </InputGroup>
                  {passwordError && <Text color='red.500' fontSize="sm" mt={1}>{passwordError}</Text>}
                </FormControl>

                {formError && <Text color='red.500' textAlign="center">{formError}</Text>}

                <Stack spacing={6} width="100%" pt={2}>
                  <Flex
                    direction={{ base: 'column', sm: 'row' }}
                    align={'center'}
                    justify={'space-between'}>
                    <Checkbox isChecked={rememberMe} onChange={(e) => setRememberMe(e.target.checked)}>
                      Remember me
                    </Checkbox>
                    <ChakraLink as={ReactRouterLink} to='/forgot-password' color={'blue.400'}>
                      Forgot password?
                    </ChakraLink>
                  </Flex>
                  <Button type="submit" colorScheme={'blue'} variant={'solid'}>
                    Sign in
                  </Button>
                  <Text align="center">
                    Don't have an account?{' '}
                    <ChakraLink as={ReactRouterLink} to='/register' color={'blue.400'}>
                      Register
                    </ChakraLink>
                  </Text>
                </Stack>
              </VStack>
            </form>
          </Box>
        </VStack>
      </Box>
    </Flex>
  );
};

export default LoginPage;